import React from "react";

import { css } from "@emotion/react";

import { subHeadlineStyles } from "../styles";
import { PlpSparePartFinderShowSpareParts } from "@swo/storefront";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { useAsyncValue } from "../../../hooks";
import { SparePartsLoadingScreen } from "./spare-parts-loading-screen";
import { PlpSitemapLinker } from "../sitemap/plp-sitemap";
import { SparePartsNavigation } from "../shared/spare-parts-navigation";
import { SparePartsFinderBottomNavigation } from "../shared/spare-parts-finder-bottom-navigation";
import { fontStyles } from "../../../styles/fonts";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { CSS_MQ, MQ_DISPLAY } from "../../../styles/breakpoints";
import { LeftHandSideImage } from "../shared/left-hand-side-image";
import { SparePartsProductListingPageTranslation } from "@swo/storefront";
import { T } from "../../../components/T";

export const ShowSparePartsProductLevel: React.FC<{
  pathname: string;
  fetcher: ({ productKey }: { productKey: string }) => Promise<PlpSparePartFinderShowSpareParts>;
  sitemapLinker: PlpSitemapLinker;
  data: { productKey: string; subfamilyKey: string; categoryKey: string };
  translations: SparePartsProductListingPageTranslation;
}> = props => {
  const {
    data: { subfamilyKey, categoryKey, productKey },
    sitemapLinker,
    translations: t,
  } = props;

  return useAsyncValue(() => props.fetcher({ productKey }), { dataKey: productKey }).fold({
    onLoading: () => SparePartsLoadingScreen,
    onError: err => <span>Error</span>,
    onLoaded: ({ selectedProduct, selectedSubfamily, selectedCategory, selectedFamily, spareParts }) => {
      const selectedProductImage = selectedProduct.image;
      const backLink = sitemapLinker("subfamily", { categoryKey, subfamilyKey });

      const sparePartsLink = sitemapLinker("productSpareParts", {
        categoryKey,
        subfamilyKey,
        productKey: selectedProduct.key,
      });

      const hasAnySpareParts = spareParts.length > 0;

      const showSparePartsElement = (
        <>
          {hasAnySpareParts ? (
            <SwarovskiButton variant={"green"} as={"a"} isInternal link={sparePartsLink}>
              <T>{t.showSpareParts}</T>
            </SwarovskiButton>
          ) : (
            <span css={subHeadlineStyles}>
              <T>{t.thereAreNoSparePartsForYourProduct}</T>
            </span>
          )}
        </>
      );

      return (
        <>
          <SparePartsNavigation
            BackNavigation={<SparePartsFinderBottomNavigation link={backLink} translations={t} />}
            category={selectedCategory}
            subfamily={selectedSubfamily}
            product={selectedProduct}
            haveAnySpareParts={hasAnySpareParts}
            translations={t}
          />

          <ContentContainer>
            <div>
              <div
                css={css`
                  ${CSS_MQ.atLeastSm} {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);

                    grid-column-gap: 20px;
                    grid-template-rows: auto auto;
                  }

                  ${CSS_MQ.atLeastMd} {
                    grid-column-gap: 40px;
                  }
                `}
              >
                <LeftHandSideImage
                  image={selectedProductImage}
                  attachStyles={css`
                    ${CSS_MQ.atMostXs} {
                      margin-bottom: 10px;
                      max-width: 220px;
                    }
                  `}
                />

                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                  `}
                >
                  <div
                    css={css`
                      display: grid;

                      ${CSS_MQ.atLeastLg} {
                        grid-template-columns: auto 1fr;
                      }

                      gap: 6px 15px;

                      ${CSS_MQ.atMostMd} {
                        gap: 4px;

                        ._label {
                          margin-bottom: 10px;
                        }
                      }
                    `}
                  >
                    <span css={[fontStyles.headline4]}>
                      <T>{t.model}</T>
                    </span>
                    <span className={"_label"}>{selectedProduct.name}</span>
                    <span css={[fontStyles.headline4]}>
                      <T>{t.productFamily}</T>
                    </span>
                    <span className={"_label"}>{selectedSubfamily.name}</span>
                    <span css={[fontStyles.headline4]}>
                      <T>{t.category}</T>
                    </span>
                    <span className={"_label"}>{selectedCategory.name}</span>
                  </div>

                  <div
                    css={[
                      MQ_DISPLAY.onlyAtLeastMd,
                      css`
                        margin-top: 20px;
                        margin-bottom: 60px;
                      `,
                    ]}
                  >
                    {showSparePartsElement}
                  </div>
                </div>

                <div
                  css={css`
                    display: none;

                    ${CSS_MQ.atMostSm} {
                      display: block;
                      grid-column: 1 / span 2;
                      margin-top: 20px;
                      margin-bottom: 20px;
                      justify-self: center;
                    }
                  `}
                >
                  {showSparePartsElement}
                </div>
              </div>

              <SparePartsFinderBottomNavigation link={backLink} centered={true} translations={t} />
            </div>
          </ContentContainer>
        </>
      );
    },
  });
};
